import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import toast from "react-hot-toast";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";
const ORDER_API = `${API_BASE_URL}/api/orders`;

function OrdersManager() {
  const token = localStorage.getItem("token");
  
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modal states
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [pendingStatus, setPendingStatus] = useState(""); 

  // Pagination, Search, and Filter State
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 10;

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(ORDER_API, {
        params: { page, limit, search, status: statusFilter },
        headers: { Authorization: `Bearer ${token}` },
      });
      setOrders(data?.orders || []);
      setTotalPages(data?.pagination?.totalPages || 1);
    } catch (err) {
      console.error(err);
      toast.error("Unable to fetch orders.");
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter, token]);

  useEffect(() => {
    setPage(1); 
    const delayDebounceFn = setTimeout(() => {
      fetchOrders();
    }, 300);
    return () => clearTimeout(delayDebounceFn);
  }, [search, statusFilter, fetchOrders]);

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      setOrders((prev) =>
        prev.map((order) =>
          order._id === orderId ? { ...order, status: newStatus } : order
        )
      );

      if (selectedOrder && selectedOrder._id === orderId) {
        setSelectedOrder((prev) => ({ ...prev, status: newStatus }));
      }

      const { data } = await axios.patch(
        `${ORDER_API}/${orderId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
      );

      toast.success(data.message || `Order marked as ${newStatus}`);
    } catch (err) {
      console.error(err);
      toast.error("Failed to update order status.");
      fetchOrders();
    }
  };

  // UPDATED: Specific colors for every single status!
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "placed": return "#8b5cf6"; // Purple (New)
      case "processing": return "#f59e0b"; // Orange (Working on it)
      case "shipped": return "#3b82f6"; // Blue (In transit)
      case "delivered": return "#10b981"; // Green (Done)
      case "cancelled": return "#ef4444"; // Red (Failed/Cancelled)
      default: return "#6b7280"; // Gray fallback
    }
  };

  const resolveImageUrl = (img) => {
    if (!img) return "";
    if (img.startsWith("http://") || img.startsWith("https://")) return img;
    let cleanPath = img.startsWith("/uploads/") ? img.replace("/uploads/", "") : img.replace("uploads/", "");
    const baseUrl = API_BASE_URL.endsWith("/") ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
    return `${baseUrl}/uploads/${cleanPath}`;
  };

  const openOrderDetails = (order) => {
    setSelectedOrder(order);
    setPendingStatus(order.status || "placed");
  };

  return (
    <div className="manager-section">
      <div className="manager-topbar">
        <div>
          <h2>Order Management</h2>
          <p>Track customer orders, update shipping statuses, and manage fulfillment.</p>
        </div>
      </div>

      <div className="manager-tools" style={{ display: "flex", gap: "15px", marginBottom: "20px" }}>
        <div className="manager-search-wrap" style={{ flex: 1 }}>
          <input
            type="text"
            className="manager-search"
            style={{ width: "100%", padding: "10px", borderRadius: "6px", border: "1px solid #ccc" }}
            placeholder="Search by Order ID, Name, or Email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="manager-filter-wrap">
          <select
            className="manager-select"
            style={{ padding: "10px", borderRadius: "6px", border: "1px solid #ccc" }}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All Orders</option>
            <option value="placed">Placed (New)</option>
            <option value="Processing">Processing</option>
            <option value="Shipped">Shipped</option>
            <option value="Delivered">Delivered</option>
            <option value="Cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      <div className="manager-table-wrap">
        <table className="manager-table">
          <thead>
            <tr>
              <th>Order ID</th>
              <th>Date</th>
              <th>Customer</th>
              <th>Total Amount</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="6" style={{ textAlign: "center", padding: "2rem" }}>Loading orders...</td>
              </tr>
            ) : orders.length === 0 ? (
              <tr>
                <td colSpan="6" style={{ textAlign: "center", padding: "2rem" }}>No orders found.</td>
              </tr>
            ) : (
              orders.map((order) => (
                <tr key={order._id}>
                  <td style={{ fontFamily: "monospace", fontWeight: "bold", fontSize: "14px" }}>
                    {order.orderId ? order.orderId : `#${order._id.slice(-6).toUpperCase()}`}
                  </td>
                  <td>{new Date(order.createdAt).toLocaleDateString()}</td>
                  <td>
                    <strong>{order.shippingAddress?.fullName || order.user?.name || "Guest"}</strong>
                    <div style={{ fontSize: "0.85em", color: "#666" }}>
                      {order.shippingAddress?.email || order.user?.email}
                    </div>
                  </td>
                  <td style={{ fontWeight: "bold" }}>₹{order.totalAmount || 0}</td>
                  <td>
                    <span
                      style={{
                        backgroundColor: getStatusColor(order.status),
                        color: "white",
                        padding: "5px 10px",
                        borderRadius: "12px",
                        fontSize: "12px",
                        fontWeight: "bold",
                        textTransform: "capitalize",
                        display: "inline-block",
                        minWidth: "75px",
                        textAlign: "center"
                      }}
                    >
                      {order.status || "Pending"}
                    </span>
                  </td>
                  <td>
                    <button 
                      className="outline-btn" 
                      onClick={() => openOrderDetails(order)}
                      style={{ padding: "6px 12px", fontSize: "13px" }}
                    >
                      View Details
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "20px", padding: "10px", backgroundColor: "#f9fafb", borderRadius: "8px" }}>
          <button 
            className="outline-btn" 
            disabled={page === 1} 
            onClick={() => setPage(p => p - 1)}
          >
            ← Previous
          </button>
          <span style={{ fontWeight: "bold", color: "#374151" }}>
            Page {page} of {totalPages}
          </span>
          <button 
            className="outline-btn" 
            disabled={page === totalPages} 
            onClick={() => setPage(p => p + 1)}
          >
            Next →
          </button>
        </div>
      )}

      {selectedOrder && (
        <div className="modal-backdrop" onClick={() => setSelectedOrder(null)}>
          <div className="modal-card" style={{ maxWidth: "970px", padding: "24px" }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-head" style={{ borderBottom: "1px solid #eee", paddingBottom: "15px", marginBottom: "20px" }}>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <h3 style={{ margin: 0 }}>
                    Order {selectedOrder.orderId ? selectedOrder.orderId : `#${selectedOrder._id.slice(-6).toUpperCase()}`}
                  </h3>
                  {/* Added a badge in the modal header so it's instantly visible */}
                  <span
                    style={{
                      backgroundColor: getStatusColor(selectedOrder.status),
                      color: "white",
                      padding: "4px 10px",
                      borderRadius: "12px",
                      fontSize: "12px",
                      fontWeight: "bold",
                      textTransform: "capitalize"
                    }}
                  >
                    {selectedOrder.status || "Placed"}
                  </span>
                </div>
                <p style={{ margin: "5px 0 0", color: "#666", fontSize: "14px" }}>
                  Placed on {new Date(selectedOrder.createdAt).toLocaleString()}
                </p>
              </div>
              <button type="button" className="modal-close" onClick={() => setSelectedOrder(null)}>✕</button>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", marginBottom: "25px" }}>
              <div style={{ backgroundColor: "#f9fafb", padding: "15px", borderRadius: "8px" }}>
                <h4 style={{ margin: "0 0 10px 0", color: "#374151" }}>Shipping Details</h4>
                <p style={{ margin: "4px 0", fontWeight: "bold" }}>{selectedOrder.shippingAddress?.fullName}</p>
                <p style={{ margin: "4px 0", color: "#4b5563", fontSize: "14px" }}>📞 {selectedOrder.shippingAddress?.phone}</p>
                <p style={{ margin: "4px 0", color: "#4b5563", fontSize: "14px" }}>✉️ {selectedOrder.shippingAddress?.email || selectedOrder.user?.email}</p>
                <p style={{ margin: "8px 0 0 0", color: "#4b5563", fontSize: "14px", lineHeight: "1.5" }}>
                  {selectedOrder.shippingAddress?.address}<br/>
                  {selectedOrder.shippingAddress?.city}, {selectedOrder.shippingAddress?.state} {selectedOrder.shippingAddress?.zipCode}
                </p>
              </div>

              <div style={{ backgroundColor: "#f9fafb", padding: "15px", borderRadius: "8px" }}>
                <h4 style={{ margin: "0 0 10px 0", color: "#374151" }}>Payment & Status</h4>
                <p style={{ margin: "4px 0", fontSize: "14px" }}><strong>Method:</strong> {selectedOrder.paymentMethod || "COD"}</p>
                <div style={{ display: "flex", justifyContent: "space-between", margin: "8px 0 4px", fontSize: "14px" }}>
                  <span>Subtotal:</span> <span>₹{selectedOrder.subtotal || 0}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", margin: "4px 0", fontSize: "14px" }}>
                  <span>Shipping:</span> <span>₹{selectedOrder.shippingFee || 0}</span>
                </div>
                {selectedOrder.discount > 0 && (
                  <div style={{ display: "flex", justifyContent: "space-between", margin: "4px 0", fontSize: "14px", color: "#10b981" }}>
                    <span>Discount:</span> <span>-₹{selectedOrder.discount}</span>
                  </div>
                )}
                <div style={{ display: "flex", justifyContent: "space-between", margin: "8px 0 15px", fontSize: "16px", fontWeight: "bold" }}>
                  <span>Total:</span> <span>₹{selectedOrder.totalAmount || 0}</span>
                </div>
                
                <hr style={{ border: "none", borderTop: "1px solid #e5e7eb", margin: "10px 0" }} />
                
                <div style={{ marginTop: "15px" }}>
                  <label style={{ display: "block", fontSize: "13px", fontWeight: "bold", marginBottom: "5px" }}>Update Order Status:</label>
                  
                  {/* UPDATED: The select dropdown dynamically changes color based on what you pick! */}
                  <select
                    className="manager-select"
                    style={{ 
                      width: "100%", 
                      padding: "10px", 
                      fontWeight: "bold", 
                      backgroundColor: getStatusColor(pendingStatus), // Uses the new color mapper!
                      color: "white", 
                      marginBottom: "10px",
                      border: "none",
                      borderRadius: "6px",
                      cursor: "pointer"
                    }}
                    value={pendingStatus}
                    onChange={(e) => setPendingStatus(e.target.value)}
                  >
                    {/* Inline styles applied to options so they remain readable when the dropdown is open */}
                    <option style={{ backgroundColor: "white", color: "#333" }} value="placed">Placed (New)</option>
                    <option style={{ backgroundColor: "white", color: "#333" }} value="Processing">Processing</option>
                    <option style={{ backgroundColor: "white", color: "#333" }} value="Shipped">Shipped</option>
                    <option style={{ backgroundColor: "white", color: "#333" }} value="Delivered">Delivered</option>
                    <option style={{ backgroundColor: "white", color: "#333" }} value="Cancelled">Cancelled</option>
                  </select>

                  <button 
                    type="button" 
                    className="theme-btn" 
                    style={{ 
                      width: "100%", 
                      padding: "10px", 
                      borderRadius: "6px",
                      opacity: pendingStatus === selectedOrder.status ? 0.6 : 1,
                      cursor: pendingStatus === selectedOrder.status ? "not-allowed" : "pointer"
                    }}
                    disabled={pendingStatus === selectedOrder.status}
                    onClick={() => handleStatusChange(selectedOrder._id, pendingStatus)}
                  >
                    {pendingStatus === selectedOrder.status ? "Status Up to Date" : "Save Status Change"}
                  </button>
                </div>
              </div>
            </div>

            <div>
              <h4 style={{ margin: "0 0 10px 0", color: "#374151" }}>Items Purchased</h4>
              <table className="manager-table" style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ backgroundColor: "#f3f4f6", textAlign: "left" }}>
                    <th style={{ padding: "10px", fontSize: "13px" }}>Product</th>
                    <th style={{ padding: "10px", fontSize: "13px", textAlign: "center" }}>Qty</th>
                    <th style={{ padding: "10px", fontSize: "13px", textAlign: "right" }}>Price</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedOrder.items?.map((item, index) => (
                    <tr key={index} style={{ borderBottom: "1px solid #eee" }}>
                      <td style={{ padding: "10px", display: "flex", alignItems: "center", gap: "12px" }}>
                        <img 
                          src={resolveImageUrl(item.image)} 
                          alt={item.name} 
                          style={{ width: "40px", height: "40px", objectFit: "cover", borderRadius: "4px" }} 
                        />
                        <span style={{ fontSize: "14px", fontWeight: "500" }}>{item.name}</span>
                      </td>
                      <td style={{ padding: "10px", textAlign: "center", fontSize: "14px" }}>{item.quantity}</td>
                      <td style={{ padding: "10px", textAlign: "right", fontSize: "14px", fontWeight: "bold" }}>₹{item.price * item.quantity}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

          </div>
        </div>
      )}
    </div>
  );
}

export default OrdersManager;